import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Loader2, Video, Copy, FileText, Sparkles, Mail } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import type { Application } from '@core/types';
import { PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';
import { interviewEventService } from '@core/supabase/services/interviewEventService';
import {
  AI_INTERVIEW_RECORDINGS_BUCKET,
  aiInterviewSessionService,
  type AiInterviewSessionType,
  type AiInterviewSessionWithArtifacts,
  type AiInterviewStoredMessage,
} from '@core/supabase/services/aiInterviewSessionService';
import { AI_INTERVIEW_LANGUAGES } from '@modules/public/ai-interview/interviewPrepConstants';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { Modal } from '@shared/components/ui/Modal';
import {
  evaluateAiInterviewEligibility,
  getEligibleApplicationsForAiInterview,
} from './aiInterviewEligibility';
import { listInterviewAttempts } from '@modules/ai-interviews/aiInterviewArtifacts';

/** Section card: titled group with optional hint (AI interviews tab). */
function AiInterviewSection({
  title,
  hint,
  children,
  className = '',
  tone = 'card',
}: {
  title: string;
  hint?: string;
  children: ReactNode;
  className?: string;
  /** `inline` = divider + heading only (use inside session cards to avoid stacked boxes). */
  tone?: 'card' | 'inline';
}) {
  if (tone === 'inline') {
    return (
      <section className={`pt-3 first:pt-0 border-t border-[var(--vscode-border)]/55 first:border-t-0 min-w-0 ${className}`}>
        <h3 className="text-[11px] font-semibold tracking-wide vscode-text-secondary">{title}</h3>
        {hint ? <p className="text-[10px] vscode-text-tertiary mt-1 leading-snug">{hint}</p> : null}
        <div className="mt-2 min-w-0">{children}</div>
      </section>
    );
  }
  return (
    <section
      className={`rounded-lg border border-[var(--vscode-border)]/85 bg-[var(--vscode-bg-secondary)]/25 overflow-hidden ${className}`}
    >
      <div className="px-3 py-2 border-b border-[var(--vscode-border)]/60 bg-[var(--vscode-bg)]/35">
        <h3 className="text-[11px] font-semibold tracking-wide vscode-text-secondary">{title}</h3>
        {hint ? <p className="text-[10px] vscode-text-tertiary mt-1 leading-snug">{hint}</p> : null}
      </div>
      <div className="p-3 min-w-0">{children}</div>
    </section>
  );
}

const SESSION_TYPE_LABEL: Record<AiInterviewSessionType, string> = {
  screening: 'Screening',
  technical: 'Technical',
  behavioral: 'Behavioral',
};

function aiSessionPipelineRound(t: AiInterviewSessionType): number {
  if (t === 'screening') return 1;
  if (t === 'technical') return 2;
  return 3;
}

function sessionStatusLabel(status: string): string {
  const m: Record<string, string> = {
    draft: 'Draft',
    invited: 'Invited',
    in_progress: 'In progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };
  return m[status] ?? status;
}

/** mailto — opens the user’s mail client; no open/click tracking (use Email hub for that). */
function buildAiInterviewMailto(p: {
  to: string;
  candidateName: string;
  prepUrl: string;
  jobTitle?: string;
}) {
  const subject = `AI interview — ${p.jobTitle ?? 'your session'}`;
  const body = `Hi ${p.candidateName},

Please complete your AI interview in the browser using this link:

${p.prepUrl}

The chat works even if you skip optional video. Reply if you have questions.

Thank you.`;
  return `mailto:${encodeURIComponent(p.to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

type Props = {
  candidateId: string;
  candidateName: string;
  candidateEmail: string | null | undefined;
  applications: Application[];
  onOpenApplicationsTab: () => void;
  /** When set (e.g. AI interview session detail), highlights this session card. */
  highlightSessionId?: string;
  /**
   * On the AI interview session detail page: Created / Completed for the current session live in the page Context rail;
   * prep / notify are available on other tabs — keep this list to session details + artifacts only.
   */
  embedInSessionDetailPage?: boolean;
};

export function CandidateAiInterviewsPanel({
  candidateId,
  candidateName,
  candidateEmail,
  applications,
  onOpenApplicationsTab,
  highlightSessionId,
  embedInSessionDetailPage = false,
}: Props) {
  const { user } = useAuth();
  const perms = usePermissions();
  const [aiInterviewSessions, setAiInterviewSessions] = useState<AiInterviewSessionWithArtifacts[]>([]);
  const [aiInterviewLang, setAiInterviewLang] = useState('en-IN');
  const [aiInterviewType, setAiInterviewType] = useState<AiInterviewSessionType>('screening');
  const [aiInterviewBusy, setAiInterviewBusy] = useState(false);
  const [aiInterviewMessage, setAiInterviewMessage] = useState<string | null>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>('');
  /** After successful create — deep-link to Pipeline email compose with application context. */
  const [trackedComposeApplicationId, setTrackedComposeApplicationId] = useState<string | null>(null);

  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcriptModal, setTranscriptModal] = useState<{
    messages: AiInterviewStoredMessage[];
    title: string;
    subtitle?: string;
  } | null>(null);

  const [videoOpen, setVideoOpen] = useState(false);
  const [videoSession, setVideoSession] = useState<AiInterviewSessionWithArtifacts | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);

  const eligibility = useMemo(
    () => evaluateAiInterviewEligibility(applications, aiInterviewType),
    [applications, aiInterviewType]
  );

  const eligibleApps = useMemo(
    () => getEligibleApplicationsForAiInterview(applications, aiInterviewType),
    [applications, aiInterviewType]
  );

  /** Single Transcript / Video attribute rows for every attempt across all sessions (newest session first). */
  const aggregatedArtifacts = useMemo(() => {
    const transcriptRows: Array<{
      key: string;
      globalIndex: number;
      session: AiInterviewSessionWithArtifacts;
      messages: AiInterviewStoredMessage[];
      contextLine: string;
    }> = [];
    const videoRows: Array<{
      key: string;
      globalIndex: number;
      session: AiInterviewSessionWithArtifacts;
      path: string;
      contextLine: string;
    }> = [];

    let ti = 0;
    let vi = 0;
    for (const s of aiInterviewSessions) {
      const attempts = listInterviewAttempts(s);
      const ctx = `${SESSION_TYPE_LABEL[s.session_type]} · ${formatDateTimeGmt(s.created_at)}`;
      for (const att of attempts) {
        ti += 1;
        transcriptRows.push({
          key: `${s.id}-agg-tr-${att.index}`,
          globalIndex: ti,
          session: s,
          messages: att.messages,
          contextLine: ctx,
        });
        const p = att.recordingPath?.trim();
        if (p) {
          vi += 1;
          videoRows.push({
            key: `${s.id}-agg-vid-${att.index}`,
            globalIndex: vi,
            session: s,
            path: p,
            contextLine: ctx,
          });
        }
      }
    }
    return { transcriptRows, videoRows };
  }, [aiInterviewSessions]);

  /** On session detail embed, the open session is summarized in the page Context rail — do not repeat it here. */
  const sessionsRenderedAsCards = useMemo(() => {
    if (embedInSessionDetailPage && highlightSessionId) {
      return aiInterviewSessions.filter((s) => s.id !== highlightSessionId);
    }
    return aiInterviewSessions;
  }, [aiInterviewSessions, embedInSessionDetailPage, highlightSessionId]);

  useEffect(() => {
    if (!user?.orgId || !candidateId) return;
    aiInterviewSessionService
      .listForCandidate(user.orgId, candidateId, 20)
      .then(setAiInterviewSessions)
      .catch(() => setAiInterviewSessions([]));
  }, [user?.orgId, candidateId]);

  useEffect(() => {
    if (eligibleApps.length === 0) {
      setSelectedApplicationId('');
      return;
    }
    setSelectedApplicationId((prev) => {
      if (prev && eligibleApps.some((a) => a.id === prev)) return prev;
      return eligibleApps[0].id;
    });
  }, [eligibleApps]);

  useEffect(() => {
    if (!videoOpen || !videoSession?.recording_storage_path?.trim()) {
      setVideoUrl(null);
      return;
    }
    const path = videoSession.recording_storage_path.trim();
    let cancelled = false;
    setVideoLoading(true);
    setVideoUrl(null);
    aiInterviewSessionService
      .getRecordingSignedUrl(path)
      .then((url) => {
        if (!cancelled) setVideoUrl(url);
      })
      .catch(() => {
        if (!cancelled) setVideoUrl(null);
      })
      .finally(() => {
        if (!cancelled) setVideoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [videoOpen, videoSession]);

  const openTranscriptModal = (messages: AiInterviewStoredMessage[], title: string, subtitle?: string) => {
    setTranscriptModal({ messages, title, subtitle });
    setTranscriptOpen(true);
  };

  const openVideoWithPath = (sessionRow: AiInterviewSessionWithArtifacts, storagePath: string) => {
    const p = storagePath.trim();
    if (!p) return;
    setVideoSession({ ...sessionRow, recording_storage_path: p });
    setVideoOpen(true);
  };

  /** Same back-target as Email compose (`Pipeline → Applications → … → Compose`); return lands on this tab. */
  const emailHubComposeTo = (applicationId: string) =>
    `/emails/compose?applicationId=${encodeURIComponent(applicationId)}&returnTo=${encodeURIComponent(
      `/candidates/${candidateId}?tab=ai-interviews`,
    )}`;

  if (!perms.canEditCandidateParameters) {
    return (
      <p className="text-sm vscode-text-secondary">
        Your role cannot access AI interview management for this candidate.
      </p>
    );
  }

  const selectedApp = applications.find((a) => a.id === selectedApplicationId);
  const canCreate =
    perms.canEditCandidate &&
    eligibility.ok &&
    Boolean(selectedApplicationId && user?.orgId && candidateId);

  return (
    <div className="space-y-4 min-w-0">
      {/* Schedule — business rules */}
      <div className="ggj-param-section-block border border-[var(--vscode-border)] rounded-lg overflow-hidden">
        <div className="ggj-profile-section-h flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[var(--vscode-text-link)] shrink-0" aria-hidden />
          Schedule AI interview
        </div>
        <div className="ggj-profile-section-body space-y-4">
          {!eligibility.ok && (
            <div
              className="text-xs rounded-md border border-[color-mix(in_srgb,var(--vscode-text-link)_26%,var(--vscode-border-primary))] bg-[color-mix(in_srgb,var(--vscode-text-link)_9%,var(--vscode-bg-secondary))] px-3 py-2 vscode-text-secondary"
              role="status"
            >
              {eligibility.message}{' '}
              <button
                type="button"
                className="text-[var(--vscode-text-link)] underline font-medium"
                onClick={onOpenApplicationsTab}
              >
                Open Applications tab
              </button>{' '}
              to advance the pipeline.
            </div>
          )}

          <AiInterviewSection title="New interview link">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-3">
              <div className="flex flex-row items-center gap-3 min-w-0">
                <span className="text-sm font-semibold vscode-text-secondary shrink-0 w-[10.5rem] min-w-[8.5rem]">
                  Link to application <span className="text-red-400">*</span>
                </span>
                <select
                  id="ai-int-app"
                  className="input text-sm flex-1 min-w-0"
                  value={selectedApplicationId}
                  onChange={(e) => setSelectedApplicationId(e.target.value)}
                  disabled={!perms.canEditCandidate || eligibleApps.length === 0}
                  aria-label="Link to application"
                >
                  {eligibleApps.length === 0 ? (
                    <option value="">No eligible application</option>
                  ) : (
                    eligibleApps.map((a) => (
                      <option key={a.id} value={a.id}>
                        {(a.job?.title ?? 'Role').slice(0, 56)}
                        {a.client?.name ? ` · ${a.client.name}` : ''} ·{' '}
                        {PIPELINE_STATUS_LABEL[a.status] ?? a.status}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="flex flex-row items-center gap-3 min-w-0">
                <span className="text-sm font-semibold vscode-text-secondary shrink-0 w-[10.5rem] min-w-[8.5rem]">
                  Interview round
                </span>
                <select
                  id="ai-int-round"
                  className="input text-sm flex-1 min-w-0"
                  value={aiInterviewType}
                  disabled={!perms.canEditCandidate}
                  onChange={(e) => setAiInterviewType(e.target.value as AiInterviewSessionType)}
                  aria-label="Interview round"
                >
                  <option value="screening">Screening</option>
                  <option value="technical">Technical</option>
                  <option value="behavioral">Behavioral</option>
                </select>
              </div>
              <div className="flex flex-row items-center gap-3 min-w-0 lg:col-span-2">
                <span className="text-sm font-semibold vscode-text-secondary shrink-0 w-[10.5rem] min-w-[8.5rem]">
                  Language
                </span>
                <select
                  id="ai-int-lang"
                  className="input text-sm flex-1 min-w-0"
                  value={aiInterviewLang}
                  disabled={!perms.canEditCandidate}
                  onChange={(e) => setAiInterviewLang(e.target.value)}
                  aria-label="Interview language"
                >
                  {AI_INTERVIEW_LANGUAGES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </AiInterviewSection>

          {selectedApp && (
            <AiInterviewSection title="Selected application snapshot">
              <dl className="ggj-attr-grid text-sm -mx-1 px-1">
                <dt className="!font-semibold">Stage</dt>
                <dd>
                  <span className="font-medium">{PIPELINE_STATUS_LABEL[selectedApp.status] ?? selectedApp.status}</span>
                </dd>
                <dt className="!font-semibold">Client</dt>
                <dd>{selectedApp.client?.name ?? '—'}</dd>
                <dt className="!font-semibold">Job</dt>
                <dd>
                  {selectedApp.job?.title ?? '—'}
                  {selectedApp.job?.id ? (
                    <Link
                      to={`/jobs/${selectedApp.job.id}`}
                      className="ml-2 text-[var(--vscode-text-link)] text-xs hover:underline"
                    >
                      View job
                    </Link>
                  ) : null}
                </dd>
              </dl>
            </AiInterviewSection>
          )}

          {!perms.canEditCandidate && (
            <p className="text-xs rounded-md border border-[color-mix(in_srgb,var(--vscode-text-link)_26%,var(--vscode-border-primary))] bg-[color-mix(in_srgb,var(--vscode-text-link)_9%,var(--vscode-bg-secondary))] px-2 py-1.5 vscode-text-secondary">
              Your role can view sessions; only recruiters (junior and above) can generate new links.
            </p>
          )}

          {aiInterviewMessage && <p className="text-xs text-brand-400/90">{aiInterviewMessage}</p>}
          {trackedComposeApplicationId && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Link
                to={emailHubComposeTo(trackedComposeApplicationId)}
                className="btn-primary text-xs px-3 py-2 inline-flex items-center gap-2 font-semibold"
              >
                <Mail className="w-3.5 h-3.5 shrink-0" aria-hidden />
                Open tracked email (Pipeline flow)
              </Link>
              <button
                type="button"
                className="text-[11px] text-[var(--vscode-text-link)] hover:underline"
                onClick={() => setTrackedComposeApplicationId(null)}
              >
                Dismiss
              </button>
            </div>
          )}

          <button
            type="button"
            className="btn-primary text-sm px-4 py-2.5 min-h-[44px] w-full sm:w-auto justify-center gap-2 font-semibold disabled:opacity-50 inline-flex items-center"
            disabled={aiInterviewBusy || !canCreate}
            title={!eligibility.ok ? eligibility.message : undefined}
            onClick={async () => {
              if (!user?.orgId || !candidateId || !selectedApplicationId || !selectedApp) return;
              setAiInterviewMessage(null);
              setAiInterviewBusy(true);
              try {
                const created = await aiInterviewSessionService.createSession({
                  orgId: user.orgId,
                  candidateId,
                  applicationId: selectedApplicationId,
                  jobId: selectedApp.job_id ?? null,
                  language: aiInterviewLang,
                  sessionType: aiInterviewType,
                  createdByOrgUserId: user.orgUserId ?? null,
                });
                const prepUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/interview/prep/${created.access_token}`;
                try {
                  await interviewEventService.createLifecycleEntry({
                    org_id: user.orgId,
                    application_id: selectedApplicationId,
                    round: aiSessionPipelineRound(aiInterviewType),
                    notes: [
                      `AI interview (browser, ${SESSION_TYPE_LABEL[aiInterviewType]}).`,
                      `Candidate prep link: ${prepUrl}`,
                      `ggj_b2b_ai_interview_sessions.id = ${created.id}`,
                      'Placeholder appears under Pipeline → Interviews alongside other interview work; slot/time is N/A until you schedule a live round.',
                    ].join('\n'),
                    scheduling_meta: {
                      source: 'ai_interview_browser',
                      ai_interview_session_id: created.id,
                    },
                  });
                } catch (calErr) {
                  console.warn('[CandidateAiInterviewsPanel] Interview calendar placeholder failed:', calErr);
                }
                const list = await aiInterviewSessionService.listForCandidate(user.orgId, candidateId, 20);
                setAiInterviewSessions(list);
                setTrackedComposeApplicationId(selectedApplicationId);
                setAiInterviewMessage(
                  'Interview link created. A Pipeline interview placeholder was registered (Data → Interviews). Send a tracked email next.',
                );
              } catch (e) {
                setAiInterviewMessage(e instanceof Error ? e.message : 'Could not create.');
              } finally {
                setAiInterviewBusy(false);
              }
            }}
          >
            {aiInterviewBusy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
                Creating…
              </>
            ) : (
              <>
                <Video className="w-4 h-4 shrink-0 opacity-95" aria-hidden />
                Create interview link
              </>
            )}
          </button>
        </div>
      </div>

      {/* Sessions */}
      <div className="ggj-param-section-block border border-[var(--vscode-border)] rounded-lg overflow-hidden">
        <div className="ggj-profile-section-h">Interview sessions</div>
        <div className="ggj-profile-section-body">
          {aiInterviewSessions.length === 0 ? (
            <p className="text-sm vscode-text-tertiary italic px-3 py-4">
              No sessions yet. Eligible pipeline stages unlock scheduling above. See the org-wide list under{' '}
              <Link to="/ai-interviews" className="text-[var(--vscode-text-link)] hover:underline font-medium">
                Data → AI interviews
              </Link>
              .
            </p>
          ) : (
            <>
              <div className="border-b border-[var(--vscode-border)] px-3 py-3 space-y-3 bg-[var(--vscode-bg-secondary)]/35">
                <div className="mb-1">
                  <p className="text-[11px] font-semibold tracking-wide vscode-text-secondary">
                    All sessions — artifacts
                  </p>
                  <p className="text-[10px] vscode-text-tertiary mt-1 leading-snug">
                    Every attempt across invites (newest sessions first). Retakes on the same link add lines in both columns.
                  </p>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  <AiInterviewSection
                    className="bg-[var(--vscode-bg)]/20"
                    title="Transcripts"
                    hint={aggregatedArtifacts.transcriptRows.length === 0 ? 'None yet.' : `${aggregatedArtifacts.transcriptRows.length} transcript(s).`}
                  >
                    {aggregatedArtifacts.transcriptRows.length === 0 ? (
                      <span className="vscode-text-tertiary italic text-sm">No transcripts yet.</span>
                    ) : (
                      <ul className="space-y-2.5 list-none m-0 p-0">
                        {aggregatedArtifacts.transcriptRows.map((row) => (
                          <li
                            key={row.key}
                            className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 border-b border-[var(--vscode-border)]/40 pb-2 last:border-0 last:pb-0"
                          >
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm min-w-0">
                              <span className="font-medium text-[var(--vscode-text-link)]">Transcript {row.globalIndex}</span>
                              <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-55" aria-hidden />
                              {row.messages.length > 0 ? (
                                <button
                                  type="button"
                                  className="text-[var(--vscode-text-link)] hover:underline font-medium inline-flex items-center gap-1"
                                  onClick={() =>
                                    openTranscriptModal(
                                      row.messages,
                                      `Transcript ${row.globalIndex}`,
                                      row.contextLine,
                                    )
                                  }
                                >
                                  <FileText className="w-3.5 h-3.5 shrink-0" aria-hidden />
                                  View ({row.messages.length} {row.messages.length === 1 ? 'turn' : 'turns'})
                                </button>
                              ) : (
                                <span className="text-xs vscode-text-tertiary italic">No text stored</span>
                              )}
                            </div>
                            <span className="text-[10px] vscode-text-tertiary tabular-nums shrink-0 sm:text-right sm:max-w-[11rem] sm:pl-2">
                              {row.contextLine}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </AiInterviewSection>
                  <AiInterviewSection
                    className="bg-[var(--vscode-bg)]/20"
                    title="Recordings"
                    hint={
                      aggregatedArtifacts.videoRows.length === 0
                        ? 'No files in storage.'
                        : `${aggregatedArtifacts.videoRows.length} file(s). Bucket below.`
                    }
                  >
                    {aggregatedArtifacts.videoRows.length === 0 ? (
                      <span className="vscode-text-tertiary italic text-sm">No recordings in storage yet.</span>
                    ) : (
                      <div className="space-y-2.5">
                        {aggregatedArtifacts.videoRows.map((row) => (
                          <div
                            key={row.key}
                            className="flex flex-col gap-2 border-b border-[var(--vscode-border)]/40 pb-2 last:border-0 last:pb-0"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                              <div className="flex flex-wrap items-start gap-x-1.5 gap-y-1 text-sm min-w-0 flex-1">
                                <span className="font-medium text-[var(--vscode-text-link)]">Recording {row.globalIndex}</span>
                                <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-55 mt-0.5" aria-hidden />
                                <div className="min-w-0 flex-1 space-y-1">
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <button
                                      type="button"
                                      className="text-[var(--vscode-text-link)] hover:underline font-medium inline-flex items-center gap-1"
                                      onClick={() => openVideoWithPath(row.session, row.path)}
                                    >
                                      <Video className="w-3.5 h-3.5 shrink-0" aria-hidden />
                                      Play recording
                                    </button>
                                    <button
                                      type="button"
                                      className="text-[11px] text-[var(--vscode-text-link)] hover:underline"
                                      onClick={async () => {
                                        try {
                                          await navigator.clipboard.writeText(row.path);
                                          setAiInterviewMessage('Storage path copied.');
                                        } catch {
                                          setAiInterviewMessage('Copy failed.');
                                        }
                                      }}
                                    >
                                      Copy path
                                    </button>
                                  </div>
                                  <span className="text-[11px] vscode-text-tertiary font-mono block truncate max-w-full">
                                    {row.path}
                                  </span>
                                </div>
                              </div>
                              <span className="text-[10px] vscode-text-tertiary tabular-nums shrink-0 sm:text-right sm:max-w-[11rem] sm:pl-2">
                                {row.contextLine}
                              </span>
                            </div>
                          </div>
                        ))}
                        <p className="text-[11px] vscode-text-tertiary pt-1 border-t border-[var(--vscode-border)]/50">
                          Bucket:{' '}
                          <code className="px-1 py-0.5 rounded bg-[var(--vscode-bg)] text-[10px]">
                            {AI_INTERVIEW_RECORDINGS_BUCKET}
                          </code>
                        </p>
                      </div>
                    )}
                  </AiInterviewSection>
                </div>
              </div>

              {sessionsRenderedAsCards.length > 0 ? (
              <div className="space-y-4 px-3 py-4">
            {sessionsRenderedAsCards.map((s) => {
              const href = `${typeof window !== 'undefined' ? window.location.origin : ''}/interview/prep/${s.access_token}`;
              const initiator = s.initiator;
              const linkedApp = s.application_id ? applications.find((a) => a.id === s.application_id) : undefined;
              const isHighlighted = Boolean(highlightSessionId && s.id === highlightSessionId);
              const showTimingAside =
                !embedInSessionDetailPage || !isHighlighted;
              const showSessionActions = !embedInSessionDetailPage;

              if (embedInSessionDetailPage) {
                return (
                  <article
                    key={s.id}
                    className="rounded-lg border border-[var(--vscode-border)]/90 bg-[var(--vscode-bg-secondary)]/35 overflow-hidden"
                  >
                    <div className="px-3 py-2.5">
                      <Link
                        to={`/ai-interviews/${s.id}?tab=ai-interviews`}
                        className="text-xs font-medium text-[var(--vscode-text-link)] hover:underline"
                      >
                        Open session workspace →
                      </Link>
                      <p className="text-[10px] vscode-text-tertiary mt-1 leading-snug tabular-nums">
                        {SESSION_TYPE_LABEL[s.session_type]} · {sessionStatusLabel(s.status)} · {formatDateTimeGmt(s.created_at)}
                      </p>
                    </div>
                  </article>
                );
              }

              return (
                <article
                  key={s.id}
                  className={`rounded-lg border bg-[var(--vscode-bg-secondary)]/50 overflow-hidden ${
                    isHighlighted
                      ? 'border-violet-500/70 ring-2 ring-violet-500/35'
                      : 'border-[var(--vscode-border)]'
                  }`}
                >
                  <div
                    className={
                      showTimingAside
                        ? 'flex flex-col lg:flex-row lg:items-stretch'
                        : 'flex flex-col'
                    }
                  >
                    <div
                      className={`flex-1 min-w-0 p-3 space-y-3 ${
                        showTimingAside ? 'border-b lg:border-b-0 lg:border-r border-[var(--vscode-border)]/55' : ''
                      }`}
                    >
                      <AiInterviewSection
                        tone="inline"
                        title="Session details"
                        hint="Artifacts for every attempt are summarized above."
                      >
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between gap-2 items-start">
                            <span className="vscode-text-tertiary shrink-0">Interviewer</span>
                            <span className="font-medium vscode-text-primary text-right min-w-0">
                              <span className="inline-flex items-center gap-1 justify-end">
                                <Sparkles className="w-3 h-3 text-violet-400 shrink-0" aria-hidden />
                                AI interviewer (browser)
                              </span>
                            </span>
                          </div>
                          <div className="flex justify-between gap-2 items-start">
                            <span className="vscode-text-tertiary shrink-0">Scheduled by</span>
                            <span className="font-medium vscode-text-primary text-right min-w-0">
                              {initiator ? (
                                <Link
                                  to={`/team/member/${initiator.id}`}
                                  className="text-[var(--vscode-text-link)] hover:underline font-medium"
                                >
                                  {initiator.display_name?.trim() || initiator.email || 'Team member'}
                                </Link>
                              ) : (
                                <span className="vscode-text-tertiary italic font-normal">Not recorded</span>
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between gap-2 items-start">
                            <span className="vscode-text-tertiary shrink-0">Language</span>
                            <span className="font-medium vscode-text-primary text-right">{s.language}</span>
                          </div>
                          <div className="flex justify-between gap-2 items-start">
                            <span className="vscode-text-tertiary shrink-0">Application</span>
                            <span className="font-medium vscode-text-primary text-right min-w-0">
                              {linkedApp ? (
                                <>
                                  <Link
                                    to={`/applications/${linkedApp.id}`}
                                    className="text-[var(--vscode-text-link)] hover:underline font-medium"
                                  >
                                    {linkedApp.job?.title ?? 'Application'}
                                  </Link>{' '}
                                  <span className="vscode-text-tertiary font-normal">
                                    ({PIPELINE_STATUS_LABEL[linkedApp.status] ?? linkedApp.status})
                                  </span>
                                </>
                              ) : s.application_id ? (
                                <span className="font-mono">{s.application_id.slice(0, 8)}…</span>
                              ) : (
                                <span className="vscode-text-tertiary italic font-normal">Not linked (legacy)</span>
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between gap-2 items-start pt-0.5 border-t border-[var(--vscode-border)]/45">
                            <span className="vscode-text-tertiary shrink-0">Session</span>
                            <span className="font-medium vscode-text-primary text-right">
                              {SESSION_TYPE_LABEL[s.session_type]} · {sessionStatusLabel(s.status)}
                            </span>
                          </div>
                        </div>
                      </AiInterviewSection>
                    </div>
                    {showTimingAside ? (
                      <aside
                        className="shrink-0 lg:w-[min(17rem,38%)] px-3 py-3 bg-[var(--vscode-bg)]/25 space-y-3 text-xs"
                        aria-label="Session timing"
                      >
                        <div>
                          <p className="text-[10px] font-semibold tracking-wide vscode-text-tertiary mb-0.5">Created</p>
                          <p className="vscode-text-secondary tabular-nums leading-snug">
                            {formatDateTimeGmt(s.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold tracking-wide vscode-text-tertiary mb-0.5">Completed at</p>
                          <p className="vscode-text-secondary tabular-nums leading-snug">
                            {s.completed_at ? formatDateTimeGmt(s.completed_at) : '—'}
                          </p>
                          {s.completed_too_short ? (
                            <p className="text-[11px] vscode-text-tertiary mt-1">Short attempt</p>
                          ) : null}
                        </div>
                      </aside>
                    ) : null}
                  </div>

                  {showSessionActions ? (
                    <div className="p-3 space-y-3 border-t border-[var(--vscode-border)]/55">
                      <AiInterviewSection tone="inline" title="Candidate prep link">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-2 min-w-0">
                          <code className="text-xs break-all flex-1 min-w-0 vscode-text-secondary leading-relaxed">{href}</code>
                          <button
                            type="button"
                            className="btn-secondary text-xs px-2 py-1.5 inline-flex items-center gap-1 shrink-0 self-start"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(href);
                                setAiInterviewMessage('Link copied.');
                              } catch {
                                setAiInterviewMessage('Copy failed.');
                              }
                            }}
                          >
                            <Copy className="w-3 h-3" aria-hidden />
                            Copy
                          </button>
                        </div>
                      </AiInterviewSection>

                      <AiInterviewSection
                        tone="inline"
                        title="Notify & track"
                        hint="mailto does not record opens. Use Email hub for delivery tracking when the session has an application."
                      >
                        <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                          {candidateEmail ? (
                            <a
                              href={buildAiInterviewMailto({
                                to: candidateEmail,
                                candidateName,
                                prepUrl: href,
                                jobTitle: linkedApp?.job?.title ?? undefined,
                              })}
                              className="btn-secondary text-xs px-3 py-2 inline-flex items-center justify-center gap-2 font-semibold"
                            >
                              <Mail className="w-3.5 h-3.5 shrink-0" aria-hidden />
                              Email candidate (mailto)
                            </a>
                          ) : (
                            <span className="text-xs vscode-text-tertiary italic">No candidate email on file — add on Profile.</span>
                          )}
                          {s.application_id ? (
                            <Link
                              to={emailHubComposeTo(s.application_id)}
                              className="btn-secondary text-xs px-3 py-2 inline-flex items-center justify-center gap-2 font-semibold"
                            >
                              <Mail className="w-3.5 h-3.5 shrink-0 opacity-80" aria-hidden />
                              Compose in Email hub (tracked)
                            </Link>
                          ) : (
                            <span className="text-xs vscode-text-tertiary leading-snug">
                              Tracked Email hub sends need an <strong className="text-[var(--vscode-foreground)]">application</strong> on the
                              session — use mailto for this row, then schedule new links with an application.
                            </span>
                          )}
                        </div>
                      </AiInterviewSection>
                    </div>
                  ) : null}
                </article>
              );
            })}
              </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Transcript modal */}
      <Modal
        open={transcriptOpen}
        onClose={() => {
          setTranscriptOpen(false);
          setTranscriptModal(null);
        }}
        title={transcriptModal?.title ? `Transcript · ${transcriptModal.title}` : 'Transcript'}
        subtitle={
          transcriptModal
            ? [candidateName, candidateEmail, transcriptModal.subtitle].filter(Boolean).join(' · ')
            : undefined
        }
        icon={<FileText className="w-4 h-4 text-white" />}
        size="lg"
      >
        {transcriptModal ? <TranscriptBody messages={transcriptModal.messages} /> : null}
      </Modal>

      {/* Video modal */}
      <Modal
        open={videoOpen}
        onClose={() => {
          setVideoOpen(false);
          setVideoSession(null);
          setVideoUrl(null);
        }}
        title={videoSession ? `Recording · ${SESSION_TYPE_LABEL[videoSession.session_type]}` : 'Recording'}
        subtitle={
          videoSession
            ? `${candidateName}${candidateEmail ? ` · ${candidateEmail}` : ''}`
            : undefined
        }
        icon={<Video className="w-4 h-4 text-white" />}
        size="xl"
      >
        {videoLoading && (
          <div className="flex items-center justify-center py-12 gap-2 text-sm vscode-text-secondary">
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
            Loading video…
          </div>
        )}
        {!videoLoading && videoUrl && (
          <video controls className="w-full max-h-[70vh] rounded-md border border-[var(--vscode-border)] bg-black" src={videoUrl}>
            <track kind="captions" />
          </video>
        )}
        {!videoLoading && !videoUrl && videoSession?.recording_storage_path && (
          <p className="text-sm text-red-400/90">Could not load a signed URL for this recording.</p>
        )}
      </Modal>
    </div>
  );
}

function TranscriptBody({ messages }: { messages: AiInterviewStoredMessage[] }) {
  if (messages.length === 0) {
    return <p className="text-sm vscode-text-tertiary">No messages.</p>;
  }
  return (
    <div className="max-h-[min(70vh,520px)] overflow-y-auto space-y-3 pr-1">
      {messages.map((m, i) => (
        <div
          key={`${m.at}-${i}`}
          className={`rounded-md border p-3 ${
            m.role === 'user'
              ? 'border-[var(--vscode-text-link)]/40 bg-[var(--vscode-bg-secondary)]'
              : 'border-[var(--vscode-border)] bg-[var(--vscode-bg)]'
          }`}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-[11px] font-semibold tracking-wide vscode-text-tertiary">
              {m.role === 'user' ? 'Candidate' : 'AI interviewer'}
            </span>
            <span className="text-[10px] vscode-text-tertiary tabular-nums">{formatDateTimeGmt(m.at)}</span>
          </div>
          <p className="text-sm vscode-text-primary whitespace-pre-wrap leading-relaxed">{m.content}</p>
        </div>
      ))}
    </div>
  );
}
